import { Box, Typography, useTheme, Divider, IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import GroupIcon from "@mui/icons-material/Group";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  calculateEndTime,
  calculateEndTimes,
  convertToSeconds,
  formatTimeString,
  format_Number,
} from "../../utils/utils";
import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionActions from "@mui/material/AccordionActions";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";

const YourBookingCard = ({
  selectedDate,
  reservType,
  time,
  adult,
  child,
  kid,
  baby,
  toddler,
  selectedExtraProduct,
  languageWords,
  totalCostYourBooking,
  combinationUpsale,
  languageId,
  cardCss
}) => {
  
  const [expanded, setExpanded] = React.useState(true);
  const theme = useTheme();
  const { activityDuration } = useSelector((store) => store?.Activity);
  const { SelectedEventOrStdResType, TotalPrice } = useSelector(
    (store) => store?.Ticketing
  );
  const { languageId: selectedLanguageId } = useSelector(
    (store) => store?.Combination
  );
  const totalCount =
    (adult || 0) + (child || 0) + (kid || 0) + (toddler || 0) + (baby || 0);

  dayjs.extend(advancedFormat);
  const { widgetConfig, resType } = useSelector(
    (store) => store?.widgetSettings
  );
  const totalSum = selectedExtraProduct.reduce(
    (acc, product) => acc + product.quantity * product.price,
    0
  );

  const formattedDate = dayjs(selectedDate, "DD-MM-YYYY").format(
    "dddd, DD MMMM YYYY"
  );

  const detectDateFormat = (dateStr) => {
    if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
      return "YYYY-M-D";
    }
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      return "DD-MM-YYYY";
    }
    return null;
  };
  const parseFlexibleDate = (dateStr) => {
    const format = detectDateFormat(dateStr);
    if (!format) return null;
    return dayjs(dateStr, format, true);
  };
  const formatTranslatedDate = (selectedDate, languageWords) => {
    const parsed = parseFlexibleDate(selectedDate);
    if (!parsed || !parsed.isValid()) return selectedDate;

    const formattedDate = parsed.format("dddd, DD MMMM YYYY");

    const translatedDate = formattedDate
      .replace(
        /\b(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b/g,
        (match) =>
          languageWords?.[match === "Monday" ? "monday" : match] || match
      )
      .replace(
        /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/g,
        (match) => languageWords?.[match] || match
      );
    return translatedDate;
  };

  let { endTime } = calculateEndTimes(
    time,
    activityDuration?.timeValue
      ? convertToSeconds(activityDuration?.timeValue)
      : reservType?.duration
  );

  endTime = endTime ? endTime : "";

  return (
    <Box sx={{ mt: 1, backgroundColor: cardCss?.CardSectionBgClr }}>
      <Accordion
        elevation={0}
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          "&:before": { display: "none" },
        }}
        defaultExpanded={true}
      >
        {/* ── SUMMARY ROW ── */}
        <AccordionSummary
          expandIcon={<ExpandMoreIcon  />}
          sx={{ px: 2}}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            {/* LEFT: Name */}
            <Typography
              sx={{ fontSize: "1rem", fontWeight: 500, flex: 1 }}
              color={cardCss?.CardSectionHeadingClr}
            >
              {resType == "horeca"
                ? reservType?.name || reservType?.eventTicketName
                : resType === "activity"
                ? reservType?.name
                : resType == "ticketing"
                ? reservType?.eventTicketName
                : reservType?.reservationtypeLanguages?.find(
                    (lang) =>
                      lang?.fieldname === "name" &&
                      lang?.idlanguage === (languageId || selectedLanguageId)
                  )?.data}
            </Typography>

            {/* CENTER: Time — nowrap prevents line break */}
            <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "center", px: 1 }}>
              {resType == "ticketing" ? (
                <Typography sx={{ fontSize: "13px", maxWidth:120,color: cardCss?.CardSectionInfoClr }}>
                  {Object(reservType)?.keys?.length > 0 || selectedDate
                    ? reservType?.isStandardTicket
                      ? reservType?.eventDateAndTime 
                      : `${dayjs(selectedDate?.fromTime, "HH:mm:ss").format("HH:mm")} ${dayjs(
                          selectedDate?.toTime,
                          "HH:mm:ss"
                        ).format("HH:mm")}`
                    : "HH:MM"}
                </Typography>
              ) : (
                <Typography
                  sx={{
                    color: cardCss?.cardSectionLabelClr,
                    fontSize: "13px",
                    
                    display: "flex",
                    alignItems: "center",
                    gap: "0.3rem",
                    whiteSpace: "nowrap",
                  }}
                >
                  {languageWords?.From}:{" "}
                  <span style={{ color: cardCss?.CardSectionInfoClr }}>
                    {time.substring(0, 5)}
                  </span>
                 {""}
                  {languageWords?.To}:{" "}
                  <span style={{ color: cardCss?.CardSectionInfoClr }}>
                    {endTime}
                  </span>
                </Typography>
              )}
            </Box>

            {/* RIGHT: Price */}
            <Box sx={{ flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
              {totalCostYourBooking > 0 && (
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: cardCss?.CardSectionPriceClr,
                  }}
                >
                  &euro;{format_Number(totalCostYourBooking.toFixed(2))}
                </Typography>
              )}
            </Box>
          </Box>
        </AccordionSummary>

        <Divider />

        {/* ── DETAILS BODY ── */}
        <AccordionDetails sx={{ px: 2, py: 1.5 }}>
          <Box>
            {/* Ticketing: guest count row */}
            {resType == "ticketing" && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ width: "20px", height: "20px" }}>
                    <GroupIcon
                      sx={{ color: cardCss?.CardSectionIconClr, width: "100%", height: "100%" }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: "14px", color: cardCss?.CardSectionInfoClr }}>
                    {totalCount} X {SelectedEventOrStdResType?.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: cardCss?.CardSectionPriceClr,
                    minWidth: "60px",
                    textAlign: "right",
                    mr:2.8
                  }}
                >
                  &euro;{format_Number((TotalPrice).toFixed(2))}
                </Typography>
              </Box>
            )}

            {/* Date + guests row */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Left: date/time info */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {resType == "ticketing" ? (
                  reservType?.eventDateAndTime && (
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <AccessTimeIcon sx={{ color: cardCss?.CardSectionIconClr, fontSize: "20px" }} />
                      <Box>
                        <Typography sx={{ fontSize: "14px", color: cardCss?.CardSectionInfoClr }}>
                          {reservType?.eventDateAndTime}
                        </Typography>
                        {selectedDate && !reservType?.isStandardTicket && (
                          <Typography
                            sx={{
                              color: cardCss?.cardSectionLabelClr,
                              fontSize: "13px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              mt: 0.5,
                            }}
                          >
                            {languageWords?.From}:{" "}
                            <span style={{ fontWeight: "bold", color: cardCss?.CardSectionInfoClr }}>
                              {`${dayjs(selectedDate?.fromTime, "HH:mm:ss").format("HH:mm")}`}
                            </span>{" "}
                            {languageWords?.To}:{" "}
                            <span style={{ fontWeight: "bold", color: cardCss?.CardSectionInfoClr }}>
                              {endTime}
                            </span>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )
                ) : (
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <AccessTimeIcon sx={{ color: cardCss?.CardSectionIconClr, fontSize: "20px" }} />
                    <Typography sx={{ fontSize: "14px", color: cardCss?.CardSectionInfoClr }}>
                      {formatTranslatedDate(selectedDate, languageWords)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Right: guest count */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1,mr:3 }}>
                <Box sx={{ width: "20px", height: "20px" }}>
                  <GroupIcon
                    sx={{ color: cardCss?.CardSectionIconClr, width: "100%", height: "100%" }}
                  />
                </Box>
                <Typography sx={{ fontSize: "13px", color: cardCss?.CardSectionInfoClr }}>
                  {totalCount}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* ── Extra products ── */}
          {selectedExtraProduct?.length > 0 && (
            <>
              <Divider sx={{ my: 1.5 }} />
              {selectedExtraProduct?.map((item) => (
                <Box
                  key={item.idproduct}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <ProductionQuantityLimitsIcon
                      sx={{ color: cardCss?.CardSectionIconClr, fontSize: "20px" }}
                    />
                    <Typography sx={{ fontSize: "14px", color: cardCss?.CardSectionInfoClr }}>
                      {item?.quantity}x {item?.productName}
                    </Typography>
                  </Box>
                  {item?.price > 0 && (
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: cardCss?.CardSectionPriceClr,
                        minWidth: "60px",
                        textAlign: "right",
                        mr:3
                      }}
                    >
                      &euro;{format_Number((item?.quantity * item?.price).toFixed(2))}
                    </Typography>
                  )}
                </Box>
              ))}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default YourBookingCard;

